import { Ellipsis, ExternalLink, Flag, Mail, Palette, Settings, Shield } from "lucide-react";
import {
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/shared/components/ui/dropdown-menu";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useIsAdmin } from "@/shared/hooks/useIsAdmin";
import { useNavigate } from "react-router-dom";
import { POST_LOSS_ROUTES, AUTH_ROUTES, ADMIN_ROUTES } from "@/shared/constants/paths";
import { sentryUtils } from "@/shared/utils/sentry";
import CircularProgress from "@/shared/components/CircularProgress";

import { SidebarUserFooter } from "./SidebarUserFooter";

export function NavUser() {
  const { isLoading } = useAppContext();
  const navigate = useNavigate();
  const { isAdmin } = useIsAdmin();

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-2">
        <CircularProgress />
      </div>
    );
  }

  const handleLogout = async () => {
    try {
      window.location.href = AUTH_ROUTES.LOGOUT;
    } catch (error) {
      console.error("Logout error:", error);
      sentryUtils.captureError(error, "NavUser logout failed", undefined, "warning");
    }
  };

  const trigger = (
    <button
      className="flex items-center justify-center size-8 rounded-md hover:bg-subtle transition-colors outline-none focus-visible:outline-none"
      aria-label="Account menu"
    >
      <Ellipsis className="size-4 text-stone-700" />
    </button>
  );

  const menuItems = (
    <DropdownMenuGroup>
      {isAdmin && (
        <>
          <DropdownMenuItem asChild>
            <a
              href="https://admin.elayne.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center no-underline"
            >
              <Shield className="mr-2 h-4 w-4 text-purple-600" />
              <span className="text-purple-600 flex-1">Admin Dashboard</span>
              <ExternalLink className="ml-auto h-3 w-3 text-purple-400" />
            </a>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate(ADMIN_ROUTES.FEATURE_FLAGS)}>
            <Flag className="mr-2 h-4 w-4 text-amber-600" />
            <span className="text-amber-600">Feature Flags</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate(ADMIN_ROUTES.STYLE_GUIDE)}>
            <Palette className="mr-2 h-4 w-4 text-blue-600" />
            <span className="text-blue-600">Style Guide</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate(ADMIN_ROUTES.PIXEL_CONTENT)}>
            <Mail className="mr-2 h-4 w-4 text-teal-600" />
            <span className="text-teal-600">Pixel Content</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
        </>
      )}
      <DropdownMenuItem onClick={() => navigate(POST_LOSS_ROUTES.SETTINGS)}>
        <Settings className="mr-2 h-4 w-4" />
        Settings
      </DropdownMenuItem>
    </DropdownMenuGroup>
  );

  return <SidebarUserFooter trigger={trigger} menuItems={menuItems} onLogout={handleLogout} />;
}
